import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar';
import 'react-dates/initialize';
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'clsx';
import { FormattedMessage } from 'react-intl';
import { MessageDescriptorPropType } from '../../../prop-types/intl';
import { DropdownMenuPositionPropType } from '../../prop-types/dropdown-menu-position';
import moment from 'moment';
import { Button } from '../button';
import { DatePickerDropdown } from '../date-picker-dropdown';
import { InlineMessage, INLINE_MESSAGE_TYPES } from '../inline-message';

import style from './date-picker.module.less';

const getFirstValidDateFormat = (formats, date) => {
  const validFormat = formats.find((format) => moment(date, format, true).isValid());
  if (validFormat) {
    return moment(date, validFormat, true);
  }
};

const formatDateValue = (value, dateFormat, additionalDateFormats) => {
  const allDateFormats = [dateFormat, ...additionalDateFormats];
  const parsedSelectedDate = getFirstValidDateFormat(allDateFormats, value) || moment(value);
  const inputValue = parsedSelectedDate.isValid() ? parsedSelectedDate.format(dateFormat) : '';
  return inputValue;
};

/**
 * @deprecated Use `DatePicker` from `@mantine/dates` instead.
 * See: ui-components/readme-documentation/MIGRATION.md
 */
export class DatePicker extends React.Component {
  static propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    onChange: PropTypes.func.isRequired,
    dateFormat: PropTypes.string,
    allowPastDates: PropTypes.bool,
    anchorRight: PropTypes.bool,
    calendarPosition: DropdownMenuPositionPropType,
    className: PropTypes.string,
    hasError: PropTypes.bool,
    innerRef: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    isOutsideRange: PropTypes.func,
    shouldInputHaveFullWidth: PropTypes.bool,
    additionalDateFormats: PropTypes.arrayOf(PropTypes.string),
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    errorMessageDescriptor: MessageDescriptorPropType,
  };

  static defaultProps = {
    dateFormat: 'MM/DD/YYYY',
    allowPastDates: false,
    anchorRight: false,
    className: '',
    hasError: false,
    innerRef: '',
    isOutsideRange: null,
    shouldInputHaveFullWidth: false,
    additionalDateFormats: [],
    placeholder: 'Select a Date',
    disabled: false,
    errorMessageDescriptor: undefined,
  };

  constructor(props) {
    super(props);
    this.state = {
      inputValue: formatDateValue(props.value, props.dateFormat, props.additionalDateFormats),
    };
  }

  componentDidUpdate(prevProps) {
    const { value, dateFormat, additionalDateFormats } = this.props;
    // Sync internal state when value prop changes externally
    if (prevProps.value !== value) {
      const newInputValue = formatDateValue(value, dateFormat, additionalDateFormats);
      const currentInputValue = this.state.inputValue;
      // Only update if the formatted value is actually different
      if (newInputValue !== currentInputValue) {
        // oxlint-disable-next-line react/no-did-update-set-state
        this.setState({ inputValue: newInputValue });
      }
    }
  }

  onInputBlur = (e) => {
    const { dateFormat, allowPastDates, additionalDateFormats } = this.props;

    if (!e.target.value.length) {
      // change to empty string because value is a string
      this.onDateChange('');
      return;
    }

    const parsedDate = moment(e.target.value, dateFormat);
    if (!parsedDate.isValid()) {
      return;
    }

    if (parsedDate.isBefore(moment()) && !allowPastDates) {
      const formattedPropValue = formatDateValue(this.props.value, dateFormat, additionalDateFormats);
      this.setState({ inputValue: formattedPropValue });
      return;
    }
    this.onDateChange(parsedDate.format(dateFormat));
  };

  onInputChange = (e) => {
    this.setState({ inputValue: e.target.value });
  };

  onDateChange = (value) => {
    this.setState({ inputValue: value });
    this.props.onChange(value);
  };

  onKeyDown = (e) => {
    e.stopPropagation();
  };

  render() {
    const {
      anchorRight = false,
      className,
      isOutsideRange,
      innerRef,
      allowPastDates = false,
      dateFormat,
      shouldInputHaveFullWidth = false,
      placeholder,
      disabled = false,
      hasError = false,
      errorMessageDescriptor,
      calendarPosition = 'bottom-left',
    } = this.props;
    const { inputValue } = this.state;
    return (
      <>
        <div className={classnames(className, style['date-picker'], 'components-date-picker', { [style.disabled]: disabled })}>
          <input
            ref={innerRef}
            className={classnames(style['date-input'], {
              [style['full-width-input']]: shouldInputHaveFullWidth,
              [style['has-error']]: hasError,
            })}
            disabled={disabled}
            onBlur={this.onInputBlur}
            onChange={this.onInputChange}
            onKeyDown={this.onKeyDown}
            placeholder={placeholder}
            type='text'
            value={inputValue}
          />
          <div className={style['date-picker-button-wrapper']}>
            <DatePickerDropdown
              allowPastDates={allowPastDates}
              anchorDirection={anchorRight ? 'right' : 'left'}
              calendarPosition={calendarPosition}
              dateFormat={dateFormat}
              isOutsideRange={isOutsideRange}
              onChange={this.onDateChange}
              value={inputValue}
              verticalSpacing={-17}
            >
              <Button
                className={classnames(style['date-picker-button'], {
                  [style['has-error']]: hasError,
                })}
                disabled={disabled}
                theme='tertiary'
              >
                <FontAwesomeIcon className={style['icon-wrapper']} icon={faCalendar} />
              </Button>
            </DatePickerDropdown>
          </div>
        </div>
        {errorMessageDescriptor && (
          <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
            <FormattedMessage {...errorMessageDescriptor} />
          </InlineMessage>
        )}
      </>
    );
  }
}
